import { useEffect, useMemo, useRef, useState } from "react";
import { Search, ChevronDown, Check, X } from "lucide-react";

export function SearchableSelect({ value, onChange, options, placeholder = "Selecionar…", allLabel, testid }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
    if (!open) setQuery("");
  }, [open]);

  const selectedLabel = useMemo(() => {
    if (!value) return allLabel || placeholder;
    return options.find((o) => o.value === value)?.label || placeholder;
  }, [value, options, allLabel, placeholder]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    const digits = q.replace(/\D/g, "");
    return options.filter(
      (o) => o.label.toLowerCase().includes(q) || (digits && (o.search || "").includes(digits))
    );
  }, [query, options]);

  const pick = (v) => {
    onChange(v);
    setOpen(false);
  };

  return (
    <div className="relative w-full sm:max-w-md" ref={wrapRef} data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="input-soft w-full flex items-center justify-between gap-2 text-left"
        data-testid={testid ? `${testid}-trigger` : undefined}
      >
        <span className={`truncate ${!value ? "text-[color:var(--ink-muted)]" : ""}`}>{selectedLabel}</span>
        <ChevronDown size={16} className="shrink-0 text-[color:var(--ink-muted)]" />
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-xl border border-[color:var(--border)] bg-[color:var(--surface)] shadow-lg overflow-hidden fade-in">
          <div className="relative border-b border-[color:var(--border)]">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--ink-muted)]" />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Escreva o nome ou NIF…"
              className="w-full bg-transparent pl-9 pr-8 py-2.5 text-sm outline-none"
              data-testid={testid ? `${testid}-search` : undefined}
            />
            {query && (
              <button type="button" onClick={() => setQuery("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-[color:var(--ink-muted)] hover:text-[color:var(--ink)]">
                <X size={14} />
              </button>
            )}
          </div>
          <ul className="max-h-72 overflow-y-auto py-1" data-testid={testid ? `${testid}-list` : undefined}>
            {allLabel && (
              <li>
                <button type="button" onClick={() => pick("")} className="w-full flex items-center justify-between gap-2 px-3 py-2 text-sm text-left hover:bg-[color:var(--paper)]">
                  <span>{allLabel}</span>
                  {!value && <Check size={15} className="text-[color:var(--primary)]" />}
                </button>
              </li>
            )}
            {filtered.length === 0 ? (
              <li className="px-3 py-6 text-center text-sm text-[color:var(--ink-muted)]">Sem resultados</li>
            ) : (
              filtered.map((o) => (
                <li key={o.value}>
                  <button
                    type="button"
                    onClick={() => pick(o.value)}
                    className="w-full flex items-center justify-between gap-2 px-3 py-2 text-sm text-left hover:bg-[color:var(--paper)]"
                    data-testid={testid ? `${testid}-option-${o.value}` : undefined}
                  >
                    <span className="truncate">{o.label}</span>
                    {value === o.value && <Check size={15} className="text-[color:var(--primary)] shrink-0" />}
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
